<template>
  <!-- Rejected: READ-ONLY review + reassurance; no editing, no resubmit CTA
       (Rolodex post-submit latch + offline human remediation). -->
  <div v-if="readOnly" class="surface">
    <div v-if="layout !== 'page'" class="sHead">
      <span class="sTitle">Business verification</span>
    </div>
    <div class="rejectBanner">
      <AppIcon name="about" :size="16" />
      <span>We’re reviewing the details — we’ll be in touch.</span>
    </div>
    <div v-if="issues.length" class="issueList">
      <span class="issueListLabel">A few details need attention:</span>
      <p v-for="(iss, i) in issues" :key="i" class="issueMsg">{{ iss.message }}</p>
    </div>
    <div class="reviewGroups">
      <div v-for="grp in reviewGroups" :key="grp.title" class="reviewGroup">
        <span class="reviewGroupTitle">{{ grp.title }}</span>
        <p v-for="(line, li) in grp.lines" :key="li" class="reviewLine">{{ line }}</p>
        <p v-for="fk in grp.fieldKeys.filter((k) => issueByField[k])" :key="fk" class="reviewIssue">
          {{ issueByField[fk] }}
        </p>
      </div>
    </div>
  </div>

  <!-- Editable 5-step wizard -->
  <WizardChrome
    v-else
    title="Verify your business"
    :step-label="stepLabel"
    :current-step="step"
    :total-steps="5"
    :layout="layout"
  >
    <!-- Step 1 — Business profile -->
    <template v-if="step === 1">
      <FormField
        label="Business name"
        :error="errors.businessName"
        hint="Up to 64 characters. If your legal name runs longer, a short form works fine here — you’ll give the exact legal name next."
        tip="A display label for the business — usually the legal name. Max 64 characters."
      >
        <input v-model="form.businessName" class="tinput" maxlength="64" @input="clearErr('businessName')" />
      </FormField>
    </template>

    <!-- Step 2 — Business information -->
    <template v-else-if="step === 2">
      <FormField label="Legal business name" :error="errors.legalBusinessName" hint="Exactly as it appears on your registration documents.">
        <input v-model="form.legalBusinessName" class="tinput" @input="clearErr('legalBusinessName')" />
      </FormField>
      <FormField label="Business type" :error="errors.businessType">
        <select v-model="form.businessType" class="tinput" @change="clearErr('businessType')">
          <option value="">Select…</option>
          <option v-for="o in BUSINESS_TYPE_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField label="Industry" :error="errors.industry">
        <select v-model="form.industry" class="tinput" @change="clearErr('industry')">
          <option value="">Select…</option>
          <option v-for="o in INDUSTRY_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField label="Registration ID type" :error="errors.registrationIdentifierType">
        <select v-model="form.registrationIdentifierType" class="tinput" @change="clearErr('registrationIdentifierType')">
          <option value="">Select…</option>
          <option v-for="o in REGISTRATION_ID_TYPE_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField
        label="Registration number"
        :error="errors.registrationNumber"
        hint="EIN: 9 digits, XX-XXXXXXX (e.g. 12-3456789) · DUNS: 9 digits"
      >
        <input v-model="form.registrationNumber" class="tinput" @input="clearErr('registrationNumber')" />
      </FormField>
      <FormField label="Region of operation" :error="errors.regionOfOperation">
        <select v-model="form.regionOfOperation" class="tinput" @change="clearErr('regionOfOperation')">
          <option v-for="o in REGION_OF_OPERATION_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField label="Website URL" :error="errors.websiteUrl">
        <input v-model="form.websiteUrl" class="tinput" placeholder="https://www.example.com" @input="clearErr('websiteUrl')" />
      </FormField>
      <FormField label="Social media profile URL" optional :error="errors.socialMediaProfileUrl">
        <input v-model="form.socialMediaProfileUrl" class="tinput" placeholder="https://www.linkedin.com/company/…" @input="clearErr('socialMediaProfileUrl')" />
      </FormField>
    </template>

    <!-- Step 3 — Business address -->
    <template v-else-if="step === 3">
      <FormField label="Name on address" :error="errors.customerName" hint="Keep it short — 16 characters max. Don’t use the full legal name.">
        <input v-model="form.customerName" class="tinput" maxlength="16" @input="clearErr('customerName')" />
      </FormField>
      <FormField label="Street address" :error="errors.street" hint="A physical address — PO boxes can’t be verified.">
        <input v-model="form.street" class="tinput" @input="clearErr('street')" />
      </FormField>
      <FormField label="Street address 2" optional>
        <input v-model="form.streetSecondary" class="tinput" placeholder="Suite / unit / floor" />
      </FormField>
      <FormField label="City" :error="errors.city">
        <input v-model="form.city" class="tinput" @input="clearErr('city')" />
      </FormField>
      <FormField label="State / province" :error="errors.region">
        <select v-model="form.region" class="tinput" @change="clearErr('region')">
          <option value="">Select…</option>
          <option v-for="o in statesForCountry(form.isoCountry)" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField label="Postal code" :error="errors.postalCode">
        <input v-model="form.postalCode" class="tinput" @input="clearErr('postalCode')" />
      </FormField>
      <FormField label="Country" :error="errors.isoCountry">
        <select v-model="form.isoCountry" class="tinput" @change="onCountryChange">
          <option v-for="o in COUNTRY_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
    </template>

    <!-- Step 4 — Authorized representative -->
    <template v-else-if="step === 4">
      <p class="stepIntro">A real person we can contact to confirm the business.</p>
      <FormField label="First name" :error="errors.firstName">
        <input v-model="form.firstName" class="tinput" @input="clearErr('firstName')" />
      </FormField>
      <FormField label="Last name" :error="errors.lastName">
        <input v-model="form.lastName" class="tinput" @input="clearErr('lastName')" />
      </FormField>
      <FormField label="Email" :error="errors.email">
        <input v-model="form.email" class="tinput" type="email" @input="clearErr('email')" />
      </FormField>
      <FormField label="Phone number" :error="errors.phoneNumber" hint="Add the country code if it’s outside the US or Canada.">
        <PhoneInput v-model="form.phoneNumber" @input="clearErr('phoneNumber')" />
      </FormField>
      <FormField label="Business title" :error="errors.businessTitle" hint="Their actual title, in your words.">
        <input v-model="form.businessTitle" class="tinput" @input="clearErr('businessTitle')" />
      </FormField>
      <FormField label="Job position" :error="errors.jobPosition" hint="The closest standard category.">
        <select v-model="form.jobPosition" class="tinput" @change="clearErr('jobPosition')">
          <option value="">Select…</option>
          <option v-for="o in JOB_POSITION_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
    </template>

    <!-- Step 5 — Review & submit -->
    <template v-else>
      <div class="reviewGroups">
        <div v-for="grp in reviewGroups" :key="grp.title" class="reviewGroup">
          <div class="reviewGroupHead">
            <span class="reviewGroupTitle">{{ grp.title }}</span>
            <button type="button" class="editLink" @click="goToStep(grp.step)">Edit</button>
          </div>
          <p v-for="(line, li) in grp.lines" :key="li" class="reviewLine">{{ line }}</p>
        </div>
      </div>
      <p class="lockNote">
        After you submit, these details lock while we review. You can keep working — status shows on
        the checklist, and we’ll flag anything that needs a fix.
      </p>
      <p v-if="submitError" class="errorMsg">{{ submitError }}</p>
    </template>

    <template #footer>
      <button v-if="step < 5" type="button" class="btn primary" @click="onContinue">Continue</button>
      <button v-else type="button" class="btn primary" :disabled="submitting" @click="onSubmit">
        {{ submitting ? 'Submitting…' : 'Submit for verification' }}
      </button>
      <button v-if="step === 1 && layout !== 'page'" type="button" class="btn secondary" @click="$emit('home')">Do this later</button>
      <button v-else-if="step > 1" type="button" class="btn secondary" @click="step -= 1">Back</button>
    </template>
  </WizardChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import AppIcon from '../../../../components/AppIcon.vue'
import WizardChrome from './WizardChrome.vue'
import FormField from './FormField.vue'
import PhoneInput from './PhoneInput.vue'
import { useTelephony } from '../../../composables/useTelephony'
import { formatPhone, toE164 } from '../../../utils/phone'
import {
  BUSINESS_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  REGISTRATION_ID_TYPE_OPTIONS,
  REGION_OF_OPERATION_OPTIONS,
  JOB_POSITION_OPTIONS,
  COUNTRY_OPTIONS,
  statesForCountry,
  VERIFICATION_FIELD_KEY_MAP,
} from './telephonyOptions'
import { validateVerificationStep, validateVerificationAll, type FieldErrors } from './telephonyValidation'
import {
  getTelephonyDraft,
  putTelephonyDraft,
  submitBusinessProfile,
  type TelephonyIssue,
} from '../../../../services/dialerApi'
import { APIError } from '../../../../services/api'

withDefaults(defineProps<{ layout?: 'drawer' | 'page' }>(), { layout: 'drawer' })
const emit = defineEmits<{ (e: 'home'): void }>()
const telephony = useTelephony()

type Form = Record<string, string>
const form = reactive<Form>({
  businessName: '',
  legalBusinessName: '',
  businessIdentity: 'DirectCustomer',
  businessType: '',
  industry: '',
  registrationIdentifierType: '',
  registrationNumber: '',
  regionOfOperation: 'UsaAndCanada',
  websiteUrl: '',
  socialMediaProfileUrl: '',
  customerName: '',
  street: '',
  streetSecondary: '',
  city: '',
  region: '',
  postalCode: '',
  isoCountry: 'US',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  businessTitle: '',
  jobPosition: '',
})

const step = ref(1)
const errors = ref<FieldErrors>({})
const submitting = ref(false)
const submitError = ref('')
const hydrated = ref(false)

const state = computed(() => telephony.projection.value?.business_profile.state ?? 'not_started')
const readOnly = computed(() => state.value === 'rejected')
const issues = computed<TelephonyIssue[]>(() => telephony.projection.value?.business_profile.issues ?? [])

// issue field_key → message, resolved through VERIFICATION_FIELD_KEY_MAP so both
// raw Twilio ids and our form keys land on the right field.
const issueByField = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}
  for (const iss of issues.value) {
    const fk = iss.field_key || ''
    const loc = VERIFICATION_FIELD_KEY_MAP[fk]
    if (loc) map[loc.formKey] = iss.message
  }
  return map
})

const stepLabel = computed(() => {
  const names = ['Business profile', 'Business information', 'Business address', 'Authorized representative', 'Review & submit']
  return `Step ${step.value} of 5 · ${names[step.value - 1]}`
})

const reviewGroups = computed(() => [
  { title: 'Business profile', step: 1, fieldKeys: ['businessName'], lines: [form.businessName || '—'] },
  {
    title: 'Business information',
    step: 2,
    fieldKeys: ['legalBusinessName', 'businessType', 'industry', 'registrationIdentifierType', 'registrationNumber', 'regionOfOperation', 'websiteUrl'],
    lines: [
      [form.legalBusinessName, label(BUSINESS_TYPE_OPTIONS, form.businessType), label(INDUSTRY_OPTIONS, form.industry)].filter(Boolean).join(' · ') || '—',
      [
        form.registrationIdentifierType ? `${form.registrationIdentifierType} ${form.registrationNumber}` : '',
        label(REGION_OF_OPERATION_OPTIONS, form.regionOfOperation),
        form.websiteUrl,
      ].filter(Boolean).join(' · '),
    ].filter(Boolean),
  },
  {
    title: 'Business address',
    step: 3,
    fieldKeys: ['customerName', 'street', 'city', 'region', 'postalCode', 'isoCountry'],
    lines: [
      [form.customerName, [form.street, form.streetSecondary].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—',
      [`${form.city}${form.region ? ', ' + form.region : ''} ${form.postalCode}`.trim(), label(COUNTRY_OPTIONS, form.isoCountry)].filter(Boolean).join(' · '),
    ].filter(Boolean),
  },
  {
    title: 'Authorized representative',
    step: 4,
    fieldKeys: ['firstName', 'lastName', 'email', 'phoneNumber', 'jobPosition', 'businessTitle'],
    lines: [
      [`${form.firstName} ${form.lastName}`.trim(), label(JOB_POSITION_OPTIONS, form.jobPosition), form.businessTitle].filter(Boolean).join(' · ') || '—',
      [form.email, formatPhone(form.phoneNumber)].filter(Boolean).join(' · '),
    ].filter(Boolean),
  },
])

function label(opts: Array<{ value: string; label: string }>, v: string): string {
  return opts.find((o) => o.value === v)?.label || ''
}

function clearErr(key: string) {
  if (errors.value[key]) {
    const next = { ...errors.value }
    delete next[key]
    errors.value = next
  }
}

function onCountryChange() {
  clearErr('isoCountry')
  // Region codes are country-specific — clear a now-invalid selection.
  const valid = statesForCountry(form.isoCountry).some((o) => o.value === form.region)
  if (!valid) form.region = ''
}

async function saveDraft() {
  if (!hydrated.value) return
  try {
    await putTelephonyDraft('business_profile', { ...form })
  } catch {
    /* best-effort autosave */
  }
}

let autosaveTimer: ReturnType<typeof setTimeout> | null = null
function queueAutosave() {
  if (!hydrated.value) return
  if (autosaveTimer) clearTimeout(autosaveTimer)
  autosaveTimer = setTimeout(saveDraft, 800)
}

// Deep-watch the form for autosave (guarded by `hydrated`).
watch(form, queueAutosave, { deep: true })

function goToStep(n: number) {
  void saveDraft()
  step.value = n
}

function onContinue() {
  const errs = validateVerificationStep(step.value, form)
  if (Object.keys(errs).length) {
    errors.value = errs
    return
  }
  errors.value = {}
  void saveDraft()
  step.value += 1
}

async function onSubmit() {
  const res = validateVerificationAll(form)
  if (!res.valid) {
    step.value = res.firstInvalidStep || 1
    errors.value = res.byStep[step.value] || {}
    return
  }
  submitting.value = true
  submitError.value = ''
  try {
    await saveDraft()
    await submitBusinessProfile()
    await telephony.refresh()
    emit('home')
  } catch (e) {
    if (e instanceof APIError && e.status === 422) {
      const serverIssues: TelephonyIssue[] = e.data?.detail?.issues || []
      const byStep: FieldErrors = {}
      let jump: number | null = null
      for (const iss of serverIssues) {
        const loc = VERIFICATION_FIELD_KEY_MAP[iss.field_key || '']
        if (loc) {
          byStep[loc.formKey] = iss.message
          if (jump === null) jump = loc.step
        }
      }
      submitError.value = serverIssues.length
        ? 'Some details need a fix before we can submit.'
        : 'We couldn’t submit those details — please review and try again.'
      if (jump) {
        step.value = jump
        errors.value = byStep
      }
    } else {
      submitError.value = 'We couldn’t submit right now — please try again.'
    }
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  try {
    const draft = await getTelephonyDraft('business_profile')
    const data = (draft?.data || {}) as Record<string, unknown>
    for (const k of Object.keys(form)) {
      if (typeof data[k] === 'string') form[k] = data[k] as string
    }
    form.phoneNumber = toE164(form.phoneNumber) ?? form.phoneNumber
  } catch {
    /* no draft yet */
  } finally {
    hydrated.value = true
  }
})

onBeforeUnmount(() => {
  if (autosaveTimer) clearTimeout(autosaveTimer)
  void saveDraft()
})
</script>

<style scoped>
.surface { display: flex; flex-direction: column; gap: 16px; overflow-y: auto; }
.sHead { display: flex; flex-direction: column; gap: 8px; }
.sTitle { font-family: var(--font-display); font-size: 22px; font-weight: var(--fw-semibold); letter-spacing: -0.01em; }
.stepIntro { margin: 0; font-size: var(--fs-sm); color: var(--color-text-secondary); }


.reviewGroups { display: flex; flex-direction: column; gap: var(--space-4); }
.reviewGroup { display: flex; flex-direction: column; gap: 4px; padding-bottom: var(--space-3); border-bottom: 1px solid var(--color-border-subtle); }
.reviewGroupHead { display: flex; align-items: center; justify-content: space-between; }
.reviewGroupTitle { font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.editLink { border: 0; background: transparent; padding: 0; color: var(--color-accent); font-size: var(--fs-xs); font-weight: var(--fw-medium); cursor: pointer; }
.editLink:hover { text-decoration: underline; }
.reviewLine { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-primary); }
.reviewIssue { margin: 2px 0 0; font-size: var(--fs-xs); color: var(--color-danger); }
.lockNote { margin: 0; font-size: var(--fs-xs); line-height: var(--lh-normal); color: var(--color-text-tertiary); }

.rejectBanner {
  display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-radius: var(--radius-lg);
  background: var(--color-bg-subtle); font-size: var(--fs-sm); color: var(--color-text-secondary);
}
.issueList { display: flex; flex-direction: column; gap: 6px; }
.issueListLabel { font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--color-text-secondary); }
.issueMsg { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-danger); }
.errorMsg { margin: 0; font-size: var(--fs-xs); color: var(--color-danger); }
</style>
